import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

export default function Terms() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#F7F6F2] text-[#1E2420]">
      <header className="px-6 sm:px-10 py-5 border-b border-[#E3E1D9] bg-white/70 backdrop-blur sticky top-0 z-30 flex items-center justify-between">
        <button onClick={() => navigate("/")} data-testid="terms-back" className="inline-flex items-center gap-2 text-sm text-[#5C6660] hover:text-[#1E2420]">
          <ArrowLeft size={16} /> Back to MedEasy
        </button>
        <div className="bg-white rounded-xl px-2 py-1 shadow-sm">
          <img src="/medeasy-logo.png" alt="MedEasy" className="h-9 w-auto object-contain" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#5C6660] font-semibold mb-3">Legal</div>
        <h1 className="font-[Manrope] font-bold text-4xl sm:text-5xl tracking-tight">Terms of Service</h1>
        <p className="text-[#5C6660] mt-3 text-sm">Last updated: June 17, 2026</p>

        <div className="prose prose-stone mt-10 space-y-7 text-[#1E2420] leading-relaxed">
          <section>
            <p className="text-[#3D4540]">
              Welcome to MedEasy (the &ldquo;Application&rdquo;), a service operated by <strong className="text-[#1E2420]">10203786 Manitoba Ltd.</strong> (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By accessing or using our website located at <a href="https://mymedeasy.ca" className="text-[#2C4C3B] underline">mymedeasy.ca</a> and any related services, you agree to be bound by these Terms of Service (&ldquo;Terms&rdquo;).
            </p>
          </section>

          <section>
            <h2 className="font-[Manrope] text-xl font-semibold">1. Description of Service &amp; AI Processing</h2>
            <p className="text-[#3D4540] mt-2">
              MedEasy provides digital tools powered by automated systems and advanced artificial intelligence (&ldquo;Emergent AI&rdquo;) to assist users in parsing, categorizing, and tracking personal medical expenses, mileage, and receipts. The Application is a structural tracking utility tool and does not provide financial, legal, medical, or tax advice.
            </p>
          </section>

          <section>
            <h2 className="font-[Manrope] text-xl font-semibold">2. Reliance on AI Output</h2>
            <p className="text-[#3D4540] mt-2">
              While our autonomous agents strive for high accuracy when reading receipts and blueprints of expense documents, automated data extraction is subject to variance. You are <strong className="text-[#1E2420]">solely responsible for reviewing, verifying, and confirming the accuracy</strong> of all AI-generated logs, metrics, and expense reports before submitting them to professional accountants or the Canada Revenue Agency (CRA).
            </p>
          </section>

          <section>
            <h2 className="font-[Manrope] text-xl font-semibold">3. Subscription Fees &amp; Stripe Payments</h2>
            <ul className="list-disc pl-5 text-[#3D4540] mt-2 space-y-2">
              <li><strong className="text-[#1E2420]">Pricing:</strong> Access to specific feature tiers of MedEasy requires a paid subscription billed in Canadian Dollars (CAD).</li>
              <li><strong className="text-[#1E2420]">Payment Processing:</strong> We use Stripe, Inc. to securely process transactions. By subscribing, you authorize us to charge your designated payment method via Stripe.</li>
              <li><strong className="text-[#1E2420]">Cancellations:</strong> You can cancel your subscription at any time via your dashboard settings. Fees are non-refundable due to the immediate digital provisioning of infrastructure resources.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-[Manrope] text-xl font-semibold">4. Intellectual Property</h2>
            <p className="text-[#3D4540] mt-2">
              All content, design elements, software architecture, agent middleware, code, and custom configurations used within the MedEasy Application are the exclusive intellectual property of <strong className="text-[#1E2420]">10203786 Manitoba Ltd.</strong>
            </p>
          </section>

          <section>
            <h2 className="font-[Manrope] text-xl font-semibold">5. Limitation of Liability</h2>
            <p className="text-[#3D4540] mt-2">
              To the maximum extent permitted by law, <strong className="text-[#1E2420]">10203786 Manitoba Ltd.</strong> shall not be liable for any indirect, incidental, or consequential damages, including tax penalties or audit discrepancies, resulting from errors, omissions, or processing anomalies generated by our automated AI systems.
            </p>
          </section>

          <section>
            <h2 className="font-[Manrope] text-xl font-semibold">6. Governing Law</h2>
            <p className="text-[#3D4540] mt-2">
              These Terms shall be governed by and construed in accordance with the laws of the <strong className="text-[#1E2420]">Province of Manitoba</strong> and the federal laws of Canada applicable therein.
            </p>
          </section>

          <section>
            <h2 className="font-[Manrope] text-xl font-semibold">7. Contact Information</h2>
            <ul className="list-none pl-0 text-[#3D4540] mt-2 space-y-1.5">
              <li><strong className="text-[#1E2420]">Legal Entity Name:</strong> 10203786 Manitoba Ltd.</li>
              <li><strong className="text-[#1E2420]">Registered Address:</strong> 110 11th Street, Brandon, MB, R7A 4J4, Canada</li>
              <li><strong className="text-[#1E2420]">Support Phone:</strong> <a href="tel:+12049815777" className="text-[#2C4C3B] underline">+1 (204) 981-5777</a></li>
              <li><strong className="text-[#1E2420]">Support Email:</strong> <a href="mailto:support@MyMedEasy.ca" className="text-[#2C4C3B] underline">support@MyMedEasy.ca</a></li>
            </ul>
          </section>
        </div>
      </main>

      <footer className="border-t border-[#E3E1D9] px-6 sm:px-10 py-6 text-center text-xs text-[#5C6660]">
        © {new Date().getFullYear()} 10203786 Manitoba Ltd., operating as MedEasy · Built in Canada · 10% of every payment supports Hope Air
      </footer>
    </div>
  );
}
